import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { UIService } from '@services';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  DashboardTypes,
  defaultPanelsOrder,
  DevicesStore,
  FEATURES_PANEL_TYPES,
  InstallationDetailsStore,
  InstallationDevicePanels,
  InstallationFeaturesStore,
} from '@store';
import { Feature, FeatureNames, FeaturePropertyKey, IsStringValueProperty } from '@vi-iot/client';

import { DragDropLayoutService } from './drag-drop-layout.service';

@Component({
  selector: 'app-drag-drop-mobile',
  templateUrl: './drag-drop-mobile.component.html',
  styleUrls: ['./drag-drop-mobile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DragDropMobileComponent implements OnInit, OnDestroy {
  @Input() orderKey: string; // gatewaySerial-deviceId

  @Output() doneEditing = new EventEmitter<void>();

  readonly featurePropertyKey = FeaturePropertyKey;
  readonly panelTypes = FEATURES_PANEL_TYPES;
  readonly wallboxPrefix = 'wallbox-';
  readonly dashboardTypes = DashboardTypes;
  allDragDropDevicesPanels: InstallationDevicePanels;

  deviceVariant$: Observable<Feature<IsStringValueProperty, never>>;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public installationDetailsStore: InstallationDetailsStore,
    public uiService: UIService,
    public devicesStore: DevicesStore,
    private dragDropLayoutService: DragDropLayoutService,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.installationDetailsStore.panelsOrder$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((_panels) => {
        this.allDragDropDevicesPanels = {};

        Object.keys(this.dragDropLayoutService.renderedPanels).forEach((deviceId) => {
          const renderedDevicePanels = this.dragDropLayoutService.renderedPanels[deviceId];
          const backendDevicePanels = _panels[deviceId];

          if (!!backendDevicePanels) {
            // Panels are first registered in DragDropLayoutService
            // when they render in the intallation details.

            // on mobile rearranging view...
            // When loading data from the store we want to make sure we don't
            // assign this.pannels panels which are not present on installation
            // as it breaks switching order of panels functionality

            // example
            // panels in store: 39
            // panels actually present on installation: 12
            // -> filter out all hidden panels so we can properly switch position between panels
            // result: 12 panels assigned to this.panels

            this.allDragDropDevicesPanels[deviceId] = _panels[deviceId].filter(
              (singlePanelObject) => {
                const panelName = singlePanelObject.panel;
                const panelAlreadyRendered = renderedDevicePanels.some((entry) => {
                  return entry === panelName;
                });

                return panelAlreadyRendered;
              }
            );
          } else {
            // If we can't get data from the backend render panels
            // as they are displayed by default on an installation

            this.allDragDropDevicesPanels[deviceId] = renderedDevicePanels.map((panel) => {
              return { panel };
            });
          }
        });
      });

    this.deviceVariant$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceVariant
    );
  }

  getDeviceIdFromOrderKey(orderKey: string): string {
    const parts = orderKey.split('-');
    const deviceId = parts.length > 0 ? parts[1] : parts[0];

    return deviceId;
  }

  drop(event: CdkDragDrop<string[]>, panel: string): void {
    moveItemInArray(this.allDragDropDevicesPanels[panel], event.previousIndex, event.currentIndex);
  }

  saveLayout(): void {
    // Add not rendered panels
    const payload = { ...this.allDragDropDevicesPanels };
    Object.keys(payload).forEach((deviceId) => {
      defaultPanelsOrder.forEach((panel) => {
        if (!payload[deviceId].some((payloadDevice) => payloadDevice.panel === panel.panel)) {
          payload[deviceId].push(panel);
        }
      });
    });
    this.installationDetailsStore.savePanelsOrderMobile(payload);
    this.doneEditing.emit();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
